/**
 * Analytics policy.
 *
 * Two hard rules, both enforced by code rather than convention:
 *  1. Only event names in `ANALYTICS_EVENTS` can be emitted.
 *  2. Only property keys in `ALLOWED_PROPERTY_KEYS` survive redaction. Anything
 *     else is dropped before the payload is built, so a careless call site
 *     cannot leak health answers, messages, injuries, nutrition detail or
 *     photographs.
 */

export const ANALYTICS_EVENTS = [
  'pathway_selected',
  'goal_selected',
  'pt_cta_clicked',
  'boxing_cta_clicked',
  'consultation_started',
  'consultation_submitted',
  'registration_completed',
  'booking_requested',
  'booking_accepted',
  'check_in_submitted',
  'plan_viewed',
  'workout_completed',
  'feedback_submitted',
] as const;

export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[number];

/**
 * Property keys permitted in an analytics payload. Deliberately limited to
 * non-identifying, non-clinical dimensions.
 */
export const ALLOWED_PROPERTY_KEYS = [
  'pathway',
  'goal',
  'route',
  'session_type_category',
  'delivery_mode',
  'plan_kind',
  'source',
  'quality_profile',
  'reduced_motion',
  'webgl_supported',
  'value_count',
] as const;

export type AllowedPropertyKey = (typeof ALLOWED_PROPERTY_KEYS)[number];

export type AnalyticsProperties = Partial<Record<AllowedPropertyKey, string | number | boolean>>;

/** Keys that must never appear, checked explicitly so violations are loud. */
const FORBIDDEN_KEY_PATTERN =
  /(health|medic|injur|pain|diagnos|nutrition_detail|calorie|macro|weight|measurement|photo|image|message|note|email|phone|name|address|dob|birth|token|password)/i;

export interface RedactionResult {
  properties: AnalyticsProperties;
  /** Keys that were removed, surfaced in development to catch mistakes early. */
  dropped: string[];
}

/**
 * Strips every property key that is not explicitly allow-listed. Returns the
 * dropped keys so development builds can warn about them.
 */
export function redactProperties(input: Readonly<Record<string, unknown>>): RedactionResult {
  const properties: AnalyticsProperties = {};
  const dropped: string[] = [];

  for (const [key, value] of Object.entries(input)) {
    const isAllowed = (ALLOWED_PROPERTY_KEYS as readonly string[]).includes(key);
    const isForbidden = FORBIDDEN_KEY_PATTERN.test(key);
    const isScalar =
      typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

    if (!isAllowed || isForbidden || !isScalar) {
      dropped.push(key);
      continue;
    }

    // Cap string length so a stray free-text value cannot become a payload.
    properties[key as AllowedPropertyKey] =
      typeof value === 'string' ? value.slice(0, 64) : value;
  }

  return { properties, dropped };
}

export function isAnalyticsEvent(name: string): name is AnalyticsEvent {
  return (ANALYTICS_EVENTS as readonly string[]).includes(name);
}
